//dependencies
import React from "react";
import { z } from "zod";

//components
import { FormField, FormRadioButtons, PanelFilters } from "@performyard/py-components";

import { SingleSelectEmployee } from "../../../../../../../components/Inputs/SelectEmployee";

const FormsIDidFilterSchema = z.object({
	subject: z
		.object({
			id: z.string(),
			name: z.string(),
		})
		.nullable()
		.optional(),
	status: z.string().optional(),
});

export type FormsIDidFilters = z.infer<typeof FormsIDidFilterSchema>;

export const FormsIDidStatusOptions = [
	{ value: "any", label: "Any" },
	{ value: "sign", label: "Signed off the form" },
	{ value: "fill", label: "Filled out the form" },
];

interface FormsIDidFiltersPanelProps {
	filters?: FormsIDidFilters;
	isOpen: boolean;
	onClose: () => void;
	onApplyFilters: (filters: FormsIDidFilters) => void;
}

export const FormsIDidFiltersPanel = ({ filters, isOpen, onClose, onApplyFilters }: FormsIDidFiltersPanelProps) => {
	const handleSubmit = ({
		value,
	}: {
		value: FormsIDidFilters;
		resetForm: () => void;
		setError: (field: keyof FormsIDidFilters, error: string) => void;
	}) => {
		const formattedState = { ...value };

		// Remove empty, null, or undefined fields
		Object.keys(formattedState).forEach((key) => {
			const filterValue = formattedState[key];
			if (filterValue === null || filterValue === undefined || filterValue === "") {
				delete formattedState[key];
			}
		});

		onApplyFilters(formattedState);
		onClose();
	};

	const defaultValues: FormsIDidFilters = {
		subject: filters?.subject
			? "id" in filters.subject
				? filters.subject
				: {
						id: (filters.subject as { value: string }).value,
						name: (filters.subject as { label: string }).label,
					}
			: null,
		status: filters?.status || "any",
	};

	return (
		<PanelFilters
			isOpen={isOpen}
			onClose={onClose}
			schema={FormsIDidFilterSchema}
			defaultValues={defaultValues}
			onSubmit={handleSubmit}
		>
			<div className="space-y-4">
				<FormField<{ id: string; name: string } | null>
					id="subject"
					label="Subject"
					input={(field) => (
						<SingleSelectEmployee
							placeholder="Make a selection"
							value={field.value ?? undefined}
							onChange={(employeeId, employeeName) => {
								field.onChange(
									employeeId && employeeName ? { id: employeeId, name: employeeName } : null
								);
							}}
						/>
					)}
				/>

				<FormRadioButtons id="status" label="Status" options={FormsIDidStatusOptions} />
			</div>
		</PanelFilters>
	);
};

export default FormsIDidFiltersPanel;
